// Payment Types for ITExperts Portal

// ============================================================================
// Enums
// ============================================================================

export type TransactionType =
  | 'escrow_fund'
  | 'escrow_release'
  | 'escrow_refund'
  | 'platform_fee'
  | 'payout'
  | 'adjustment'
  | 'refund';

export type TransactionStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'reversed';

export type EscrowStatus =
  | 'pending'
  | 'funded'
  | 'releasing'
  | 'released'
  | 'refunding'
  | 'refunded'
  | 'disputed'
  | 'cancelled';

export type PayoutStatus =
  | 'pending'
  | 'processing'
  | 'in_transit'
  | 'paid'
  | 'failed'
  | 'cancelled';

// ============================================================================
// Stripe Account (Expert)
// ============================================================================

export interface StripeAccount {
  id: string;
  expert_id: string;
  stripe_account_id: string;
  account_type: string;
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  requirements_current_deadline?: string;
  requirements_disabled_reason?: string;
  default_currency: string;
  country: string;
  business_type?: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Stripe Customer (Company)
// ============================================================================

export interface StripeCustomer {
  id: string;
  company_id: string;
  stripe_customer_id: string;
  billing_email?: string;
  billing_name?: string;
  billing_address?: Record<string, unknown>;
  vat_number?: string;
  tax_exempt: string;
  default_payment_method_id?: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Payment Method
// ============================================================================

export interface PaymentMethod {
  id: string;
  type: string;
  card_brand?: string;
  card_last4?: string;
  card_exp?: string;
  is_default: boolean;
  status: string;
}

// ============================================================================
// Transaction
// ============================================================================

export interface Transaction {
  id: string;
  company_id?: string;
  expert_id?: string;
  contract_id?: string;
  milestone_id?: string;
  type: TransactionType;
  status: TransactionStatus;
  amount_cents: number;
  currency: string;
  platform_fee_cents: number;
  stripe_fee_cents: number;
  net_amount_cents: number;
  description?: string;
  failure_code?: string;
  failure_message?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

// ============================================================================
// Escrow Hold
// ============================================================================

export interface EscrowHold {
  id: string;
  company_id: string;
  expert_id: string;
  contract_id?: string;
  milestone_id?: string;
  job_application_id?: string;
  amount_cents: number;
  currency: string;
  platform_fee_rate: number;
  platform_fee_cents: number;
  expert_amount_cents: number;
  status: EscrowStatus;
  description?: string;
  created_at: string;
  updated_at: string;
  funded_at?: string;
  released_at?: string;
  refunded_at?: string;
  expires_at?: string;
}

// ============================================================================
// Payout
// ============================================================================

export interface Payout {
  id: string;
  expert_id: string;
  stripe_account_id: string;
  amount_cents: number;
  currency: string;
  status: PayoutStatus;
  stripe_payout_id?: string;
  bank_name?: string;
  bank_last4?: string;
  arrival_date?: string;
  failure_code?: string;
  failure_message?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

// ============================================================================
// Expert Balance
// ============================================================================

export interface ExpertBalance {
  available_cents: number;
  pending_cents: number;
  total_earned_cents: number;
  total_withdrawn_cents: number;
  currency: string;
  can_withdraw: boolean;
  min_withdrawal_cents: number;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface OnboardExpertRequest {
  country: string;
  business_type: 'individual' | 'company';
}

export interface OnboardExpertResponse {
  account_id: string;
  onboarding_url: string;
  expires_at: number;
}

export interface ConnectStatusResponse {
  account_id: string;
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  is_fully_onboarded: boolean;
  dashboard_url?: string;
  requirements_deadline?: string;
}

export interface CreateCustomerRequest {
  billing_email: string;
  billing_name: string;
  vat_number?: string;
}

export interface AddPaymentMethodRequest {
  payment_method_id: string;
  set_default: boolean;
}

export interface FundEscrowRequest {
  expert_id: string;
  amount_cents: number;
  currency?: string;
  description: string;
  payment_method_id: string;
  contract_id?: string;
  milestone_id?: string;
  job_application_id?: string;
}

export interface ReleaseEscrowRequest {
  notes?: string;
}

export interface RequestPayoutRequest {
  amount_cents: number;
  currency?: string;
}

export interface TransactionListParams {
  page?: number;
  page_size?: number;
  type?: TransactionType;
  status?: TransactionStatus;
}

export interface TransactionListResponse {
  transactions: Transaction[];
  total: number;
  page: number;
  page_size: number;
}

export interface EscrowListResponse {
  escrows: EscrowHold[];
  total: number;
  page: number;
  page_size: number;
}

export interface PayoutListResponse {
  payouts: Payout[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format cents to currency string
 */
export function formatCurrency(cents: number, currency: string = 'EUR'): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

/**
 * Get human-readable status for escrow
 */
export function getEscrowStatusLabel(status: EscrowStatus): string {
  const labels: Record<EscrowStatus, string> = {
    pending: 'Pending',
    funded: 'Funded',
    releasing: 'Releasing',
    released: 'Released',
    refunding: 'Refunding',
    refunded: 'Refunded',
    disputed: 'Disputed',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
}

/**
 * Get human-readable status for payout
 */
export function getPayoutStatusLabel(status: PayoutStatus): string {
  const labels: Record<PayoutStatus, string> = {
    pending: 'Pending',
    processing: 'Processing',
    in_transit: 'In Transit',
    paid: 'Paid',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };
  return labels[status] || status;
}

/**
 * Get human-readable transaction type
 */
export function getTransactionTypeLabel(type: TransactionType): string {
  const labels: Record<TransactionType, string> = {
    escrow_fund: 'Escrow Funded',
    escrow_release: 'Escrow Released',
    escrow_refund: 'Escrow Refunded',
    platform_fee: 'Platform Fee',
    payout: 'Payout',
    adjustment: 'Adjustment',
    refund: 'Refund',
  };
  return labels[type] || type;
}

/**
 * Get status color for escrow
 */
export function getEscrowStatusColor(status: EscrowStatus): string {
  const colors: Record<EscrowStatus, string> = {
    pending: 'yellow',
    funded: 'blue',
    releasing: 'blue',
    released: 'green',
    refunding: 'orange',
    refunded: 'gray',
    disputed: 'red',
    cancelled: 'gray',
  };
  return colors[status] || 'gray';
}

/**
 * Get status color for payout
 */
export function getPayoutStatusColor(status: PayoutStatus): string {
  const colors: Record<PayoutStatus, string> = {
    pending: 'yellow',
    processing: 'blue',
    in_transit: 'blue',
    paid: 'green',
    failed: 'red',
    cancelled: 'gray',
  };
  return colors[status] || 'gray';
}
